import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  consent: CookieConsent | null;
  hasConsented: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updateConsent: (consent: CookieConsent) => void;
  showBanner: boolean;
  isEuUser: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

// Simple EU detection based on timezone
const detectIsEuUser = (): boolean => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const euTimezones = [
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
      'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Vienna', 'Europe/Brussels',
      'Europe/Prague', 'Europe/Warsaw', 'Europe/Budapest', 'Europe/Stockholm',
      'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Oslo', 'Europe/Dublin',
      'Europe/Lisbon', 'Europe/Athens', 'Europe/Sofia', 'Europe/Bucharest',
      'Europe/Zagreb', 'Europe/Ljubljana', 'Europe/Bratislava', 'Europe/Tallinn',
      'Europe/Riga', 'Europe/Vilnius', 'Europe/Luxembourg', 'Europe/Malta',
      'Europe/Nicosia'
    ];
    return euTimezones.includes(timezone);
  } catch {
    return false;
  }
};

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useLocalStorage<CookieConsent | null>('cookie-consent', null);
  const [isEuUser] = useState(detectIsEuUser());

  const hasConsented = consent !== null;
  const showBanner = isEuUser && !hasConsented;

  const acceptAll = () => {
    const fullConsent: CookieConsent = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setConsent(fullConsent);
  };

  const rejectAll = () => {
    const essentialOnly: CookieConsent = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setConsent(essentialOnly);
  };

  const updateConsent = (newConsent: CookieConsent) => {
    setConsent(newConsent);
  };

  const value: CookieConsentContextType = {
    consent,
    hasConsented,
    acceptAll,
    rejectAll,
    updateConsent,
    showBanner,
    isEuUser,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
